import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Check, Plus, Clock, X, MapPin, CreditCard, BadgeCheck, Star } from 'lucide-react';
import SuccessDialog from './SuccessDialog';

const API = '/api';

const BookingModal = ({ isOpen, onClose, services, initialSelectedService = null }) => {
  const { user, getAuthHeaders } = useAuth();
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStylist, setSelectedStylist] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('salon');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [billing, setBilling] = useState(null);

  const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);
  const hasFromPrice = selectedServices.some((service) => service.price_is_from);

  useEffect(() => {
    if (isOpen) {
      if (initialSelectedService) {
        setSelectedServices([initialSelectedService]);
      }
    }
  }, [isOpen, initialSelectedService]);

  useEffect(() => {
    if (selectedServices.length > 0) {
      fetchStylists(selectedServices[0].id);
    }
  }, [selectedServices]);

  useEffect(() => {
    if (!isOpen || !user) {
      setBilling(null);
      return;
    }
    axios.get(`${API}/billing/me`, { headers: getAuthHeaders() })
      .then((response) => {
        setBilling(response.data);
        if (response.data?.membership?.remaining_visits > 0) {
          setPaymentMethod('membership');
        }
      })
      .catch(() => setBilling(null));
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (selectedDate && selectedStylist) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [selectedDate, selectedStylist?.id]);

  // Pre-select favorite stylist when stylists are loaded
  useEffect(() => {
    if (stylists.length > 0 && user?.favorite_stylist_id && !selectedStylist) {
      const favoriteStylist = stylists.find(s => s.id === user.favorite_stylist_id);
      if (favoriteStylist) {
        setSelectedStylist(favoriteStylist);
      }
    }
  }, [stylists, user?.favorite_stylist_id]);

  const fetchStylists = async (serviceId) => {
    try {
      const response = await axios.get(`${API}/stylists?service_id=${serviceId}`);
      setStylists(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Stylisten');
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/bookings/slots?date=${dateStr}&stylist_id=${selectedStylist.id}`);
      setAvailableSlots(response.data.slots);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Slots');
    }
  };

  const handleServiceSelect = (service) => {
    if (service.coming_soon) {
      toast.info('Dieser Service ist bald verfügbar!');
      return;
    }
    setSelectedServices(prev => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        const updated = prev.filter(s => s.id !== service.id);
        if (updated.length === 0) {
          setSelectedStylist(null);
          setSelectedDate(null);
          setSelectedTime(null);
        }
        return updated;
      }
      if (prev.length >= 2) {
        toast.info('Maximal 2 Dienstleistungen gleichzeitig möglich');
        return prev;
      }
      return [...prev, service];
    });
  };

  const handleStylistSelect = (stylist) => {
    setSelectedStylist(selectedStylist?.id === stylist.id ? null : stylist);
  };

  const handleConfirm = async () => {
    if (!user) {
      toast.error('Bitte melden Sie sich an, um einen Termin zu buchen.');
      onClose();
      return;
    }

    if (selectedServices.length === 0 || !selectedStylist || !selectedDate || !selectedTime) {
      toast.error('Bitte wählen Sie Service, Stylist, Datum und Uhrzeit');
      return;
    }

    const serviceIds = selectedServices.map(s => s.id);
    const combinedName = selectedServices.map(s => s.name).join(' + ');

    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.post(
        `${API}/bookings`,
        {
          service_ids: serviceIds,
          stylist_id: selectedStylist.id,
          date: dateStr,
          time: selectedTime,
          notes,
          payment_method: paymentMethod,
        },
        { headers: getAuthHeaders() }
      );

      // Bookings are confirmed immediately; payment takes place at the salon.
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setBookingDetails({
          service: combinedName,
          date: format(selectedDate, 'dd.MM.yyyy'),
          time: selectedTime,
          email: user?.email,
          totalPrice,
          totalDuration,
        });
        resetAndClose();
        setShowSuccess(true);
        toast.success('Buchung erfolgreich – Bestätigung wird per E-Mail gesendet!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setSelectedServices([]);
    setSelectedStylist(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setNotes('');
    setPaymentMethod('salon');
    onClose();
  };

  const isBookableDay = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (date < today) {
      return false;
    }
    
    // Allow Tuesday(2) to Saturday(6)
    return day >= 2 && day <= 6;
  };

  const regularServices = services.filter(s => !s.coming_soon && s.price > 0);
  const comingSoonServices = services.filter(s => s.coming_soon);

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0" data-testid="booking-modal">
        <div className="flex h-[85vh]">
          {/* Left Section - Service Selection & Calendar */}
          <div className="flex-1 overflow-y-auto p-8 bg-white" style={{ maxHeight: '85vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                  Termin buchen
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Dienstleistungen → Stylist → Zeit
                </p>
              </div>
              <button
                onClick={resetAndClose}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Services Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Dienstleistungen</h3>
                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                  {selectedServices.length}/2 gewählt
                </span>
              </div>
              
              {/* Regular Services */}
              <div className="space-y-3 mb-6">
                {regularServices.map((service) => {
                  const isSelected = selectedServices.some(s => s.id === service.id);
                  return (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 mb-1">{service.name}</h4>
                        <p className="text-sm text-slate-600 mb-2">{service.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center text-slate-500">
                            <Clock className="w-4 h-4 mr-1" />
                            {service.duration} Min
                          </div>
                          <div className="font-semibold text-slate-900">
                            {service.price_is_from ? 'ab ' : ''}€{service.price}
                          </div>
                        </div>
                      </div>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {isSelected ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Plus className="w-5 h-5" />
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>

              {/* Coming Soon Services */}
              {comingSoonServices.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Bald Verfügbar</h4>
                  <div className="space-y-3">
                    {comingSoonServices.map((service) => (
                      <div
                        key={service.id}
                        className="w-full text-left p-4 rounded-xl border border-slate-200 bg-slate-50 opacity-60"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900">{service.name}</h4>
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                                Coming Soon
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{service.description}</p>
                            <div className="flex items-center space-x-4 text-sm">
                              <div className="flex items-center text-slate-500">
                                <Clock className="w-4 h-4 mr-1" />
                                {service.duration} Min
                              </div>
                              <div className="font-semibold text-slate-900">
                                {service.price_is_from ? 'ab ' : ''}€{service.price}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stylist Selection */}
            {selectedServices.length > 0 && stylists.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Wähle deinen Stylist</h3>
                <div className="grid grid-cols-2 gap-4">
                  {stylists.map((stylist) => (
                    <button
                      key={stylist.id}
                      onClick={() => handleStylistSelect(stylist)}
                      className={`p-4 rounded-xl border-2 transition-all text-left relative ${
                        selectedStylist?.id === stylist.id
                          ? 'border-blue-600 bg-blue-50 shadow-md'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      {/* Favorite Badge */}
                      {user?.favorite_stylist_id === stylist.id && (
                        <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-sm">
                          <Star className="w-3 h-3 fill-current" />
                          Favorit
                        </div>
                      )}
                      <div className="flex items-start space-x-3">
                        {stylist.profile_image_url ? (
                          <img
                            src={stylist.profile_image_url}
                            alt={stylist.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                            {stylist.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 mb-1">{stylist.name}</h4>
                          {stylist.bio && (
                            <p className="text-xs text-slate-600 line-clamp-2">{stylist.bio}</p>
                          )}
                        </div>
                        {selectedStylist?.id === stylist.id && (
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Calendar Section with Time Slots Side by Side */}
            {selectedServices.length > 0 && selectedStylist && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Datum wählen</h3>
                <div className="flex gap-6">
                  {/* Calendar on the left */}
                  <div className="flex-1">
                    <div className="border rounded-xl p-4 bg-white shadow-sm">
                      <style>{`
                        .rdp {
                          --rdp-cell-size: 45px;
                          --rdp-accent-color: #2563eb;
                          --rdp-background-color: #dbeafe;
                          margin: 0;
                        }
                        .rdp-months {
                          justify-content: center;
                        }
                        .rdp-month {
                          width: 100%;
                        }
                        .rdp-caption {
                          font-size: 1.1rem;
                          font-weight: 700;
                          margin-bottom: 1rem;
                          color: #1e293b;
                        }
                        .rdp-head_cell {
                          font-size: 0.85rem;
                          font-weight: 600;
                          color: #64748b;
                          width: 45px;
                          height: 35px;
                        }
                        .rdp-cell {
                          width: 45px;
                          height: 45px;
                        }
                        .rdp-day {
                          width: 45px;
                          height: 45px;
                          font-size: 1rem;
                          font-weight: 600;
                          border-radius: 10px;
                        }
                        .rdp-day:hover:not(.rdp-day_disabled) {
                          background-color: #dbeafe;
                        }
                        .rdp-day_selected {
                          background-color: #1e293b !important;
                          color: white !important;
                          font-weight: 700;
                        }
                        .rdp-day_disabled {
                          opacity: 0.3;
                          cursor: not-allowed;
                        }
                        .rdp-nav_button {
                          width: 32px;
                          height: 32px;
                        }
                        .rdp-nav_button svg {
                          width: 18px;
                          height: 18px;
                        }
                      `}</style>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => !isBookableDay(date)}
                        locale={de}
                        className="rounded-lg"
                        fromDate={new Date()}
                      />
                    </div>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      Verfügbar: Dienstag - Samstag, 11:30 - 18:00 Uhr
                    </p>
                  </div>

                  {/* Time Slots on the right */}
                  <div className="w-48 flex-shrink-0">
                    <div className="border rounded-xl p-4 bg-white shadow-sm h-full">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Uhrzeit</h4>
                      {!selectedDate ? (
                        <p className="text-sm text-slate-400 text-center py-4">
                          Bitte zuerst ein Datum wählen
                        </p>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-sm text-red-600">Keine Zeiten verfügbar</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedTime(slot)}
                              className={`w-full p-3 rounded-lg border text-sm font-medium transition-all ${
                                selectedTime === slot
                                  ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                              data-testid={`time-slot-${slot.replace(':', '-')}`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Section - Booking Summary */}
          <div className="w-96 bg-slate-50 border-l border-slate-200 flex flex-col" style={{ maxHeight: '85vh' }}>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Salon Info */}
              <div className="mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG"
                alt="Salon Galo"
                className="h-12 w-auto mb-3"
              />
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <span className="text-yellow-500 mr-1">★★★★★</span>
                  <span className="font-semibold text-slate-900">4.8</span>
                  <span className="text-slate-600 ml-1">(120+ Bewertungen)</span>
                </div>
                <div className="flex items-start text-sm text-slate-600">
                  <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Salon Galo, Bonn</span>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Buchungsübersicht</h3>
              
              {selectedServices.length === 0 ? (
                <p className="text-sm text-slate-500">Wählen Sie 1-2 Dienstleistungen aus</p>
              ) : (
                <div className="space-y-4">
                  {selectedServices.map((svc) => (
                    <div key={svc.id} className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-900 mb-1">{svc.name}</h4>
                        <button
                          onClick={() => handleServiceSelect(svc)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-service-${svc.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{svc.duration} Min</span>
                        <span className="font-semibold text-slate-900">{svc.price_is_from ? 'ab ' : ''}€{svc.price}</span>
                      </div>
                    </div>
                  ))}

                  {selectedStylist && (
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="text-sm">
                        <p className="text-slate-600 mb-2">Stylist</p>
                        <div className="flex items-center space-x-3">
                          {selectedStylist.profile_image_url ? (
                            <img
                              src={selectedStylist.profile_image_url}
                              alt={selectedStylist.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                              {selectedStylist.name.charAt(0)}
                            </div>
                          )}
                          <p className="font-semibold text-slate-900">{selectedStylist.name}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedDate && (
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="text-sm">
                        <p className="text-slate-600 mb-1">Datum</p>
                        <p className="font-semibold text-slate-900">
                          {format(selectedDate, 'EEEE, dd. MMMM yyyy', { locale: de })}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedTime && (
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="text-sm">
                        <p className="text-slate-600 mb-1">Uhrzeit</p>
                        <p className="font-semibold text-slate-900">{selectedTime} Uhr</p>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">Gesamtdauer</span>
                      <span className="text-sm font-semibold text-slate-700">{totalDuration} Min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Gesamtpreis</span>
                      <span className="text-xl font-bold text-slate-900">{selectedServices.some((service) => service.price_is_from) ? 'ab ' : ''}€{totalPrice}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method Selection */}
            {selectedServices.length > 0 && selectedDate && selectedTime && (
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-3 block">
                    Termin sichern
                  </label>
                  <div className="space-y-3">
                    <button
                      onClick={() => setPaymentMethod('salon')}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        paymentMethod === 'salon'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="payment-salon"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            paymentMethod === 'salon' ? 'bg-blue-600' : 'bg-slate-100'
                          }`}>
                            <CreditCard className={`w-5 h-5 ${
                              paymentMethod === 'salon' ? 'text-white' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">Im Salon bezahlen</p>
                            <p className="text-xs text-slate-600">Keine Onlinezahlung erforderlich</p>
                          </div>
                        </div>
                        {paymentMethod === 'salon' && <Check className="w-5 h-5 text-blue-600" />}
                      </div>
                    </button>

                    {!hasFromPrice ? (
                      <button
                        onClick={() => setPaymentMethod('card_full')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'card_full'
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid="payment-card-full"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              paymentMethod === 'card_full' ? 'bg-blue-600' : 'bg-slate-100'
                            }`}>
                              <CreditCard className={`w-5 h-5 ${
                                paymentMethod === 'card_full' ? 'text-white' : 'text-slate-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">Online mit Karte bezahlen</p>
                              <p className="text-xs text-slate-600">Gesamtbetrag €{totalPrice.toFixed(2)} sicher über Stripe</p>
                            </div>
                          </div>
                          {paymentMethod === 'card_full' && <Check className="w-5 h-5 text-blue-600" />}
                        </div>
                      </button>
                    ) : (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        Online-Kartenzahlung ist bei „ab“-Preisen nicht möglich, da der Endpreis erst nach der Beratung feststeht.
                      </div>
                    )}

                    {billing?.membership?.remaining_visits > 0 && (
                      <button
                        onClick={() => setPaymentMethod('membership')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'membership'
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid="payment-membership"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              paymentMethod === 'membership' ? 'bg-emerald-600' : 'bg-slate-100'
                            }`}>
                              <BadgeCheck className={`w-5 h-5 ${
                                paymentMethod === 'membership' ? 'text-white' : 'text-slate-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{billing.membership.plan?.name} verwenden</p>
                              <p className="text-xs text-slate-600">
                                Noch {billing.membership.remaining_visits} Termin(e) in diesem Monat
                              </p>
                            </div>
                          </div>
                          {paymentMethod === 'membership' && <Check className="w-5 h-5 text-emerald-600" />}
                        </div>
                      </button>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    {paymentMethod === 'card_full'
                      ? 'Nach erfolgreicher Kartenzahlung wird der Termin sofort bestätigt und per E-Mail bestätigt.'
                      : paymentMethod === 'membership'
                        ? 'Der Termin wird über Ihr aktives Abo bestätigt.'
                        : 'Der Termin wird sofort bestätigt. Die Bezahlung erfolgt beim Termin im Salon.'}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Anmerkungen (optional)
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Besondere Wünsche..."
                    className="h-20 text-sm"
                    data-testid="booking-notes"
                  />
                </div>
              </div>
            )}

            </div>
            
            {/* Action Button - Fixed at bottom */}
            <div className="p-6 pt-4 bg-slate-50 border-t border-slate-200">
              <Button
                onClick={handleConfirm}
                disabled={
                  selectedServices.length === 0 || 
                  !selectedStylist || 
                  !selectedDate || 
                  !selectedTime || 
                  loading
                }
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-full text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-booking-button"
              >
                {loading
                  ? (paymentMethod === 'card_full' ? 'Zahlung wird vorbereitet...' : 'Wird gebucht...')
                  : paymentMethod === 'membership'
                    ? 'Abo-Termin buchen'
                    : paymentMethod === 'card_full'
                      ? `€${totalPrice.toFixed(2)} sicher bezahlen`
                      : 'Termin verbindlich buchen'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
      
      <SuccessDialog
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        bookingDetails={bookingDetails}
      />
    </Dialog>
  );
};

export default BookingModal;
